import { Globe, Puzzle, Sparkles, Wrench } from 'lucide-react';

import {
    SCOPE_LABELS,
    type ScopeType,
} from '@apps/web/features/admin_agent_scratchpad/model/constants';
import { cn } from '@apps/web/lib/utils';

/**
 * Color-coded, low-saturation scope badge. Reading tool, not a dashboard —
 * saturation intentionally muted; hover only nudges background.
 */
const SCOPE_STYLE: Record<
    ScopeType,
    { readonly ring: string; readonly text: string; readonly bg: string }
> = {
    global: {
        ring: 'ring-slate-300/60 dark:ring-slate-500/40',
        text: 'text-slate-700 dark:text-slate-200',
        bg: 'bg-slate-100/70 dark:bg-slate-800/40',
    },
    organization: {
        ring: 'ring-blue-300/60 dark:ring-blue-500/40',
        text: 'text-blue-800 dark:text-blue-200',
        bg: 'bg-blue-50/70 dark:bg-blue-900/30',
    },
    tool: {
        ring: 'ring-amber-300/60 dark:ring-amber-500/40',
        text: 'text-amber-800 dark:text-amber-200',
        bg: 'bg-amber-50/70 dark:bg-amber-900/30',
    },
    skill: {
        ring: 'ring-emerald-300/60 dark:ring-emerald-500/40',
        text: 'text-emerald-800 dark:text-emerald-200',
        bg: 'bg-emerald-50/70 dark:bg-emerald-900/30',
    },
};

const SCOPE_ICON: Record<ScopeType, typeof Globe> = {
    global: Globe,
    organization: Puzzle,
    tool: Wrench,
    skill: Sparkles,
};

export function ScopeBadge({
    scopeType,
    scopeId,
    label,
    className,
}: {
    readonly scopeType: ScopeType;
    readonly scopeId?: string | null;
    readonly label?: string | null;
    readonly className?: string;
}) {
    const style = SCOPE_STYLE[scopeType];
    const Icon = SCOPE_ICON[scopeType];
    const trailing =
        scopeType === 'global'
            ? null
            : (label ?? scopeId ?? SCOPE_LABELS[scopeType]);
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 transition-colors ring-inset',
                style.bg,
                style.text,
                style.ring,
                className,
            )}
        >
            <Icon className="h-3 w-3" aria-hidden="true" />
            <span>{SCOPE_LABELS[scopeType]}</span>
            {trailing ? (
                <span className="text-muted-foreground/80 max-w-[16ch] truncate font-normal">
                    · {trailing}
                </span>
            ) : null}
        </span>
    );
}
